import { createPinia } from 'pinia'
import { createApp } from 'vue'

import App from './App.vue'
import i18n from './i18n'
import router from './router'

import Antd from 'ant-design-vue'
import 'ant-design-vue/dist/reset.css'
import 'bootstrap/dist/css/bootstrap.css'
import Toast from 'vue-toastification'
import 'vue-toastification/dist/index.css'
import VueKonva from 'vue3-konva'

// Highcharts
import * as Sentry from '@sentry/vue'
import Highcharts from 'highcharts'
import HighchartsVue from 'highcharts-vue'
import HighchartsMore from 'highcharts/highcharts-more'
import AccessibilityModule from 'highcharts/modules/accessibility'
import StockCharts from 'highcharts/modules/drilldown'
import ExportDataModule from 'highcharts/modules/export-data'
import { default as ExportingModule, default as StockCharts2 } from 'highcharts/modules/exporting'
import Gantt from 'highcharts/modules/gantt'
import Heatmap from 'highcharts/modules/heatmap'
import OrgChart from 'highcharts/modules/organization'
import PatternFill from 'highcharts/modules/pattern-fill'
import Sankey from 'highcharts/modules/sankey'
import SeriesOnPoint from 'highcharts/modules/series-on-point'
import Stock from 'highcharts/modules/stock'
import VueTelInput from 'vue-tel-input'
import 'vue-tel-input/vue-tel-input.css'
import OtpInput from 'vue3-otp-input'

import cdnDirectives from './directives/cdnDirectives'

import { sweepStaleActiveSessions } from 'src/composables/useActiveSession'
import { initAnalytics } from 'src/composables/useAnalytics'
import { HIGHCHARTS_LANG } from 'src/config/highchartsLang'

Stock(Highcharts)
StockCharts(Highcharts)
StockCharts2(Highcharts)
Sankey(Highcharts)
OrgChart(Highcharts)
Gantt(Highcharts)
Heatmap(Highcharts)
PatternFill(Highcharts)
HighchartsMore(Highcharts)
ExportingModule(Highcharts)
ExportDataModule(Highcharts)
AccessibilityModule(Highcharts)
SeriesOnPoint(Highcharts)
// BoostModule(Highcharts)

// Exporting context menu ("Print chart", "Download PNG image", ...) — Highcharts' own
// strings, not vue-i18n. A language switch reloads the app, so the initial locale is enough.
if (HIGHCHARTS_LANG[i18n.global.locale.value]) {
  Highcharts.setOptions({ lang: HIGHCHARTS_LANG[i18n.global.locale.value] })
}

// Unregister any existing service workers left over from the old PWA setup
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    registrations.forEach((reg) => reg.unregister())
  })
}

// Reload when a lazy-loaded route chunk is missing (happens when a new deploy
// removes old hashed chunks while the user still has the old app in memory).
// Guard prevents multiple simultaneous chunk failures from triggering multiple reloads.
let preloadErrorReloading = false
window.addEventListener('vite:preloadError', () => {
  if (preloadErrorReloading) return
  preloadErrorReloading = true
  window.location.reload()
})

// Drop active-duration entries from studies abandoned for 30+ days.
sweepStaleActiveSessions()

const app = createApp(App)

Sentry.init({
  app,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
  tracesSampleRate: 1.0,

  tracePropagationTargets: [],

  replaysSessionSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 0.5,

  replaysOnErrorSampleRate: 1.0
})

initAnalytics()

app.directive('cdn', cdnDirectives)

app.component('otp-input', OtpInput);
app.use(createPinia())
app.use(router)
app.use(i18n)
app.use(Antd)
app.use(VueKonva)
app.use(Toast)
app.use(HighchartsVue)
app.use(VueTelInput);
app.mount('#app')
